import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import BarraSeccion from '../../Components/BarraSeccion';
import { Insignia } from '../../Components/FilaAdmin';

import { etiquetaLicenciatura, formatearFechaHora } from '../../Helpers/solicitudes';

const EXTENSIONES = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

// Los documentos vienen como data URL (base64) o como URL; el tipo sale del prefijo data:<tipo>;
const tipoDocumento = archivo => {
    const tipo = /^data:([^;,]+)/.exec(archivo)?.[1];
    return { extension: EXTENSIONES[tipo] ?? (tipo ? tipo.split('/')[1] : 'pdf'), esImagen: Boolean(tipo?.startsWith('image/')) };
};

const vacio = valor => valor === undefined || valor === null || String(valor).trim() === '';

const Valor = ({ tipo, valor }) => {
    if (vacio(valor)) return <span className='detalle_admin_vacio'>—</span>;
    if (tipo === 'correo') return <a href={'mailto:' + valor}>{valor}</a>;
    if (tipo === 'telefono') return <a href={'tel:' + String(valor).replace(/\s/g, '')}>{valor}</a>;
    return String(valor);
};

// Detalle de una solicitud de inscripción o reinscripción. secciones: [{ titulo, campos: [{ etiqueta, valor(solicitud), tipo? }] }]
// rutaActivas / rutaArchivo: a dónde regresa "Volver" si se abrió la página directo (sin historial)
const DetalleSolicitud = ({ helper, etiqueta, licenciaturas, secciones, rutaActivas, rutaArchivo }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [solicitud, setSolicitud] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        obtener();
    }, [id]);

    const obtener = async () => {
        const res = await helper.obtenerUnData(id);
        if (res.success) setSolicitud(res.data);
        else Swal.fire('Error!', 'No se pudo obtener la solicitud, codigo: ' + res.code, 'error');
    }

    const volver = () => {
        if (window.history.state?.idx > 0) return navigate(-1);
        navigate(solicitud?.archivado ? rutaArchivo : rutaActivas);
    }

    const cambiar = async cambios => {
        setGuardando(true);
        const res = await helper.cambiarEstado(id, cambios);
        setGuardando(false);
        if (res?.success) setSolicitud(actual => ({ ...actual, ...cambios }));
        return res?.success;
    }

    const archivar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Archivar esta solicitud?',
            text: solicitud.estadoAlumno ? 'Se moverá al archivo.' : 'Todavía está pendiente. Se moverá al archivo de todos modos.',
            showCancelButton: true,
            confirmButtonText: 'Sí, archivar',
            cancelButtonText: 'Cancelar'
        });
        if (isConfirmed && (await cambiar({ archivado: true }))) {
            Swal.fire('Archivada', 'La solicitud se movió al archivo.', 'success');
        }
    }

    const descargar = (archivo, indice) => {
        const enlace = document.createElement('a');
        enlace.href = archivo;
        enlace.setAttribute('download', `${solicitud.nombre || 'solicitud'} - documento ${indice + 1}.${tipoDocumento(archivo).extension}`);
        enlace.click();
    }

    if (!solicitud) {
        return (
            <Fragment>
                <SideNav />
                <div className='lista_admin_estado'><Loader /></div>
            </Fragment>
        );
    }

    const licenciatura = etiquetaLicenciatura(licenciaturas, solicitud.licenciatura);
    const documentos = Array.isArray(solicitud.pdfs) ? solicitud.pdfs.filter(Boolean) : [];

    return (
        <Fragment>
            <SideNav />
            {guardando && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <BarraSeccion
                etiqueta={etiqueta}
                encabezado={solicitud.nombre || '(Sin nombre)'}
                ayuda={[licenciatura, 'recibida el ' + formatearFechaHora(solicitud.fecha)].filter(Boolean).join(' · ')}
                acciones={
                    <Fragment>
                        <button type='button' className='boton_admin boton_admin_secundario' onClick={volver}>
                            Volver
                        </button>
                        {!solicitud.archivado && (
                            <Fragment>
                                <button type='button' className='boton_admin boton_admin_secundario' onClick={archivar}>
                                    Archivar
                                </button>
                                <button type='button' className='boton_admin' onClick={() => cambiar({ estadoAlumno: !solicitud.estadoAlumno })}>
                                    {solicitud.estadoAlumno ? 'Marcar como pendiente' : 'Marcar como atendida'}
                                </button>
                            </Fragment>
                        )}
                    </Fragment>
                }
            />
            <div className='detalle_admin'>
                <div className='detalle_admin_estado'>
                    <Insignia variante={solicitud.estadoAlumno ? 'verde' : 'amarilla'}>{solicitud.estadoAlumno ? 'Atendida' : 'Pendiente'}</Insignia>
                    {solicitud.archivado && <Insignia variante='gris'>Archivada</Insignia>}
                </div>
                <div className='detalle_admin_rejilla'>
                    {secciones.map(seccion => (
                        <section key={seccion.titulo} className='detalle_admin_tarjeta'>
                            <h2 className='detalle_admin_titulo'>{seccion.titulo}</h2>
                            <dl className='detalle_admin_campos'>
                                {seccion.campos.map(campo => (
                                    <div key={campo.etiqueta} className='detalle_admin_campo'>
                                        <dt>{campo.etiqueta}</dt>
                                        <dd><Valor tipo={campo.tipo} valor={campo.valor(solicitud)} /></dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    ))}
                    <section className='detalle_admin_tarjeta'>
                        <h2 className='detalle_admin_titulo'>Documentos</h2>
                        {documentos.length === 0 ? (
                            <p className='detalle_admin_vacio'>No se adjuntaron documentos.</p>
                        ) : (
                            <ul className='detalle_admin_documentos'>
                                {documentos.map((archivo, indice) => {
                                    const { extension, esImagen } = tipoDocumento(archivo);
                                    return (
                                        <li key={indice}>
                                            <i className='material-icons'>{esImagen ? 'image' : 'picture_as_pdf'}</i>
                                            <span>Documento {indice + 1} <small>({extension.toUpperCase()})</small></span>
                                            <button type='button' className='boton_admin boton_admin_secundario' onClick={() => descargar(archivo, indice)}>
                                                Descargar
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </div>
            </div>
        </Fragment>
    );
};

export default DetalleSolicitud;
